// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { MessageBar, MessageBarType, PrimaryButton, Stack, Text } from '@fluentui/react';
import { ReactNode } from 'react';
import { LoginForm } from './LoginForm';
import { useAuthStatus } from '../utils/useAuthStatus';
import { useNavigate } from 'react-router-dom';

type RequireRoleProps = {
  authProvider: 'sso' | 'login';
  redirectUri: string;
  allowedRoles?: string[];
  children: (auth: { userEmail: string; userRole: string }) => ReactNode;
};

export const RequireRole = ({ authProvider, redirectUri, allowedRoles, children }: RequireRoleProps): JSX.Element => {
  const {
    isAuthenticated,
    userRole,
    userEmail,
    authError,
    hasCheckedStatus,
    setAuthError,
    handleLoginSuccess,
    handleLoginFailure
  } = useAuthStatus({
    authProvider,
    redirectUri,
    autoStart: true
  });

  const isAllowed = !allowedRoles || (!!userRole && allowedRoles.includes(userRole));
  const navigate = useNavigate();

  const login = () => {
    if (authProvider === 'sso') {
      navigate('/app/v1/login');
    } else if (authProvider === 'login') {
      navigate('/app/v2/login');
    }
  };

  if (authProvider === 'login' && !isAuthenticated) {
    return (
      <Stack tokens={{ childrenGap: 12 }} styles={{ root: { maxWidth: 720, margin: '32px auto' } }}>
        {authError && (
          <MessageBar messageBarType={MessageBarType.blocked} isMultiline={false} onDismiss={() => setAuthError(null)}>
            {authError}
          </MessageBar>
        )}
        {hasCheckedStatus && <LoginForm onLogin={handleLoginSuccess} onFail={handleLoginFailure} />}
      </Stack>
    );
  }

  if (authProvider === 'sso' && authError) {
    return (
      <Stack tokens={{ childrenGap: 12 }} styles={{ root: { maxWidth: 720, margin: '32px auto' } }}>
        <Text variant="medium">{authError}</Text>
        <PrimaryButton onClick={login}>Log in</PrimaryButton>
      </Stack>
    );
  }

  if (!isAuthenticated) {
    return <></>;
  }

  if (!isAllowed) {
    return (
      <Stack tokens={{ childrenGap: 12 }} styles={{ root: { maxWidth: 720, margin: '32px auto' } }}>
        <Text>Forbidden</Text>
      </Stack>
    );
  }

  return <>{children({ userEmail, userRole })}</>;
};
